<template>
  <!-- eslint-disable vue/no-v-model-argument -->
  <div v-if="!$apollo.queries.getAccountDetails.loading">
    <FlowHeading
      :title="$t('Tell us more about your company')"
      :sub-title="
        $t(
          'Give us a few more details to make sure you have the best experience when using Worksome.',
        )
      "
    />

    <div class="wds-input-group">
      <label>{{ $t('How many freelancers do you currently work with?') }}</label>
      <WdsSelect
        :start-selected="freelancerCount"
        class="wds-input-full"
        :placeholder="$t('Select')"
        :list="freelancerCountList"
        :error="errorFreelancerCount"
        data-test="company-workers-select"
        @select-option="handleFreelancerCountSelected"
      />
    </div>

    <div class="wds-input-group">
      <label>{{ $t("What's your role?") }}</label>
      <WdsSelect
        :start-selected="role"
        class="wds-input-full"
        :placeholder="$t('Select')"
        :list="roleList"
        :error="errorRole"
        data-test="company-role-select"
        @select-option="handleRoleSelected"
      />
    </div>

    <!-- <div class="wds-input-group">
      <label>{{ $t('How do you want to use worksome?') }}</label>
      <div class="wds-checkbox-group wds-checkbox-group--column">
        <WdsCheckbox v-model:checked="fms" :value="fms">
          {{ $t('Manage my team of freelancers') }}
        </WdsCheckbox>
        <WdsCheckbox v-model:checked="bill" :value="bill">
          {{ $t('Handle invoices and payments') }}
        </WdsCheckbox>
        <WdsCheckbox v-model:checked="compliance" :value="compliance">
          {{ $t('Automate compliance and workforce classification') }}
        </WdsCheckbox>
        <WdsCheckbox v-model:checked="hire" :value="hire">
          {{ $t('Find and hire new freelancers') }}
        </WdsCheckbox>
      </div>
    </div> -->

    <div class="action-submit">
      <WdsButton
        full-width
        lg
        primary
        :disabled="isSubmitting"
        data-test="company-info-submit-button"
        @click="callNextScreen"
      >
        <div class="tw-flex">
          <transition name="fade">
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import gql from 'graphql-tag'
import { WdsButton, WdsSelect, WdsCheckbox } from '@worksome/wds-components'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import { useGraphQlError } from '@/composables/errors'
import { FormValidation } from '@/utils/formValidation'

export default {
  name: 'CompanyInfo',

  // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
  components: { ButtonSpinner, WdsButton, WdsCheckbox, WdsSelect, FlowHeading },

  inject: ['next'],

  setup() {
    const errors = ref({})

    const errorFreelancerCount = useGraphQlError(errors, 'expected_freelancer_count')
    const errorRole = useGraphQlError(errors, 'account_owner_company_role')

    return {
      errors,
      errorFreelancerCount,
      errorRole,
    }
  },

  data() {
    return {
      companyId: null,
      freelancerCount: '',
      role: '',
      fms: false,
      bill: false,
      compliance: false,
      hire: false,
      freelancerCountList: [
        { value: '0-5', label: '0-5' },
        { value: '6-25', label: '6-25' },
        { value: '26-50', label: '26-50' },
        { value: '51-100', label: '51-100' },
        { value: '100+', label: '100+' },
        { value: "I don't know", label: this.$t("I don't know") },
      ],
      roleList: [
        { value: 'C-Suite', label: 'C-Suite' },
        { value: 'Engineering & Technical', label: 'Engineering & Technical' },
        { value: 'Finance', label: 'Finance' },
        { value: 'Human Resources', label: 'Human Resources' },
        { value: 'Procurement', label: 'Procurement' },
        { value: 'Information Technology', label: 'Information Technology' },
        { value: 'Legal', label: 'Legal' },
        { value: 'Marketing', label: 'Marketing' },
        { value: 'Operations', label: 'Operations' },
        { value: 'Sales', label: 'Sales' },
        { value: 'Other', label: 'Other' },
      ],
      isSubmitting: false,
    }
  },

  methods: {
    async updateCompanyInfo() {
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation update($input: UpdateCompanyInput!) {
              updateCompany(input: $input) {
                globalId
                marketingMetadata {
                  expected_freelancer_count
                  account_owner_company_role
                  will_use_for_fms
                  will_use_for_billing
                  will_use_for_compliance
                  will_use_for_hire
                }
              }
            }
          `,
          variables: {
            input: {
              globalId: this.companyId,
              marketingMetadata: {
                expected_freelancer_count: this.freelancerCount,
                account_owner_company_role: this.role,
                will_use_for_fms: this.fms,
                will_use_for_billing: this.bill,
                will_use_for_compliance: this.compliance,
                will_use_for_hire: this.hire,
              },
            },
          },
          context: {
            headers: {
              'X-FLOW-STEP-COMPLETED': this.$route.fullPath,
            },
          },
        })
        .catch(({ graphQLErrors }) => {
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.errors = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}
          }
        })
    },

    async callNextScreen() {
      this.isSubmitting = true
      await this.updateCompanyInfo()
      this.isSubmitting = false
      this.next()
    },

    handleFreelancerCountSelected(val) {
      this.freelancerCount = val.value
    },

    handleRoleSelected(val) {
      this.role = val.value
    },
  },

  apollo: {
    getAccountDetails: {
      query: gql`
        query me {
          me {
            activeAccount {
              globalId
              ... on Company {
                marketingMetadata {
                  expected_freelancer_count
                  account_owner_company_role
                  will_use_for_fms
                  will_use_for_billing
                  will_use_for_compliance
                  will_use_for_hire
                }
              }
            }
          }
        }
      `,
      result({ data }) {
        const info = data.me.activeAccount
        this.companyId = info.globalId
        this.freelancerCount = info.marketingMetadata?.expected_freelancer_count ?? ''
        this.role = info.marketingMetadata?.account_owner_company_role ?? ''
        this.fms = info.marketingMetadata?.will_use_for_fms ?? false
        this.bill = info.marketingMetadata?.will_use_for_billing ?? false
        this.compliance = info.marketingMetadata?.will_use_for_compliance ?? false
        this.hire = info.marketingMetadata?.will_use_for_hire ?? false
      },
    },
  },
}
</script>

<style scoped lang="scss">
label {
  font-size: 0.875rem;
  margin-bottom: 8px;
}
.wds-input-full :deep(label) {
  width: 100%;
}
.wds-input-group {
  margin: 24px 0;
}

.wds-checkbox-group {
  display: flex;

  .wds-checkbox-outer {
    margin-bottom: 16px;
  }
}

.wds-checkbox-group.wds-checkbox-group--column {
  flex-direction: column;
}

:deep(.wds-select-wrapper .wds-select-button-display) {
  padding: 0.5625rem 0.75rem;
}

.wds-checkbox-group .wds-checkbox-outer:last-child {
  margin-bottom: unset;
}

.action-submit {
  margin: 32px 0 16px;
  text-align: center;
}
</style>
